import { formatCurrency } from '@mahawi/eshop-common/dist/src/format-currency';
import { getFlag } from '@mahawi/eshop-common/dist/src/get-flag';
import {
  EEventContextAction,
  EEventType,
  type IBasketProduct,
  type ICategory,
  type ICountry,
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ICustomerCurrencyType,
  type IDelivery,
  type ILanguageType,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { Field, Form, Formik, FormikProps } from 'formik';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const SignInModal = React.lazy(() => import('./sign-in-modal'));

import { type IAccountState } from '../reducers/account/types';
import { setDestinationCountryTypeIsoCode } from '../reducers/basket/reducer';
import { type IBasketState } from '../reducers/basket/types';
import { type ICategoryState } from '../reducers/category/types';
import { setTheme, toggleSidebar } from '../reducers/config/reducer';
import { type IConfigState, type Theme } from '../reducers/config/types';
import { type ICountryState } from '../reducers/country/types';
import { type ICurrencyState } from '../reducers/currency/types';
import { eventSend } from '../reducers/event/reducer';
import { type ILanguageState } from '../reducers/language/types';
import { type IProductState } from '../reducers/product/types';
import { clearResults, query } from '../reducers/search/reducer';
import {
  changeCountryType,
  changeCurrencyType,
  changeLanguageType,
} from '../reducers/user/reducer';
import { type IUserState } from '../reducers/user/types';
import { getCategoryUrl } from '../utils/category';
import RopereLogo from './ropere-logo';

function Topbar({
  Account,
  Basket,
  Category,
  Config,
  Currency,
  dispatch,
  Language,
  Product,
  User,
  Country,
}: {
  Account: IAccountState;
  Basket: IBasketState;
  Category: ICategoryState;
  Config: IConfigState;
  Currency: ICurrencyState;
  User: IUserState;
  dispatch: Dispatch;
  Language: ILanguageState;
  Product: IProductState;
  Country: ICountryState;
}): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const currency = React.useMemo(
    () =>
      Currency.currencies.find(
        (c: ICustomerCurrencyType) => c.isoCode === User.currencyType?.isoCode,
      ),
    [Currency.currencies, User.currencyType?.isoCode],
  );

  const firstCurrency = React.useMemo(
    () => Currency.currencies[0],
    [Currency.currencies],
  );

  const {
    languageType: { code: languageCode },
    languages,
  } = Language;

  const initialValues = React.useMemo(
    () => ({
      currency: currency ? currency.isoCode : firstCurrency.isoCode,
      languageType: {
        code: languageCode,
      },
      countryType: {
        isoCode: User.countryType?.isoCode || 'CZE',
      },
    }),
    [currency, firstCurrency?.isoCode, languageCode, User.countryType?.isoCode],
  );

  const [, , path, identifier, ...restSegments] = location.pathname.split('/');

  const handleSearchQueryChange = useCallback(
    (value: string): void => {
      dispatch(
        query({
          query: value,
          languageType: {
            code: Language.languageType.code,
          },
        }),
      );
    },
    [dispatch, Language.languageType.code],
  );

  let deliveryPrice: string | undefined;
  let deliveryTime: number | undefined | null;

  if (
    User.countryType?.isoCode &&
    User.currencyType &&
    User.currencyType.isoCode &&
    Country.deliveries.length > 0
  ) {
    const delivery: IDelivery | undefined = Country.deliveries.find(
      ({ currencyType, countryType }: IDelivery): boolean =>
        currencyType.isoCode === User.currencyType?.isoCode &&
        countryType.isoCode === User.countryType?.isoCode,
    );

    if (delivery) {
      deliveryPrice = formatCurrency(delivery.price, User.currencyType);
    }

    const country: ICountry | undefined = Country.countries.find(
      ({ isoCode }: ICountry): boolean => isoCode === User.countryType?.isoCode,
    );

    if (country) {
      deliveryTime = country.delivery.time;
    }
  }

  const handleMenuToggle = useCallback((): void => {
    dispatch(toggleSidebar({}));
  }, [dispatch]);

  const [systemDark, setSystemDark] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemDark(mq.matches);
    const handler = (e: MediaQueryListEvent): void => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return (): void => mq.removeEventListener('change', handler);
  }, []);

  const effectiveTheme: Exclude<Theme, 'system'> =
    Config.theme === 'system' ? (systemDark ? 'dark' : 'light') : Config.theme;

  const handleThemeToggle = useCallback((): void => {
    const next: Theme = effectiveTheme === 'dark' ? 'light' : 'dark';
    dispatch(setTheme({ theme: next }));
    document.documentElement.setAttribute('data-theme', next);
    document.cookie = `theme=${next}; path=/; max-age=31536000; SameSite=Lax`;
  }, [effectiveTheme, dispatch]);

  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = (): void => {
      document.documentElement.style.setProperty(
        '--topbar-height',
        `${el.offsetHeight}px`,
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return (): void => ro.disconnect();
  }, []);

  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const accountNavRef = useRef<HTMLDivElement>(null);

  useEffect((): (() => void) => {
    if (!showAccountDropdown) {
      return (): void => {};
    }
    const handleOutside = (e: MouseEvent): void => {
      if (
        accountNavRef.current &&
        !accountNavRef.current.contains(e.target as Node)
      ) {
        setShowAccountDropdown(false);
      }
    };
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return (): void => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showAccountDropdown]);

  const [isScrolled, setIsScrolled] = useState(false);
  const scrollLockRef = useRef(false);

  useEffect((): (() => void) => {
    const handleScroll = (): void => {
      if (scrollLockRef.current) return;
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return (): void => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect((): (() => void) => {
    scrollLockRef.current = true;
    const timer = setTimeout((): void => {
      scrollLockRef.current = false;
    }, 400);
    return (): void => clearTimeout(timer);
  }, [isScrolled]);

  const placeholderText: string = t('layout.searchQuery');

  const formikRef = useRef<FormikProps<typeof initialValues>>(null);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'countryType.isoCode',
        User.countryType?.isoCode || 'CZE',
      );
    }
  }, [
    formikRef,
    User.countryType?.isoCode,
    Basket.billingAddress,
    Basket.deliveryAddress,
    Basket.deliveryAddressIsDifferent,
  ]);

  return (
    <>
      <header
        ref={headerRef}
        className={`topbar${isScrolled ? ' topbar--slim' : ''}`}
      >
        <Link
          to={`/${languageCode.toLowerCase()}`}
          className="logo-container"
          aria-label="Ropere - Home"
        >
          <RopereLogo type="small" />
          <span>ropere</span>
        </Link>

        <a href="mailto:info@ropere.com" className="item email">
          <svg className="mailOutlined" aria-hidden="true" focusable="false">
            <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0-8 4.99L4 6h16zm0 12H4V8l8 5 8-5v10z" />
          </svg>
          <span className="mailOutlined--text">info@ropere.com</span>
        </a>

        <input
          type="search"
          className="searchInput"
          name="searchInput"
          aria-label={placeholderText}
          placeholder={placeholderText}
          onKeyUp={(e): void => {
            if (e.key === 'Enter') {
              const value = e.currentTarget.value.trim();

              if (value.length > 0) {
                dispatch(clearResults());
                navigate(
                  `/${languageCode.toLowerCase()}/search?q=${encodeURIComponent(value)}`,
                );
              }

              return;
            }

            handleSearchQueryChange(e.currentTarget.value);

            dispatch(
              eventSend({
                type: EEventType.SEARCH_PRODUCT,
                action: EEventContextAction.SEARCH,
                context: { search: { query: e.currentTarget.value } },
              }),
            );
          }}
        />

        <div className="minicart">
          <Link
            to={`/${languageCode.toLowerCase()}/basket`}
            className="item"
            aria-label={t('navigation.title.basket')}
          >
            <svg
              className="shoppingCartOutlined"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.37-.66-.11-1.48-.87-1.48H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2h7.45zM6.16 6h12.15l-2.76 5H8.53L6.16 6zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
            <div
              className={`badge ${Basket.products.length > 0 ? '' : 'empty'}`}
            >
              {Basket.products.length}
            </div>
          </Link>

          {Basket.products.length > 0 && (
            <div className="minicart__dropdown">
              <ul className="minicart__items">
                {Basket.products.map((product: IBasketProduct) => {
                  const productPrice: ICurrencyPrice | undefined =
                    product.currencyPrices.find(
                      (c: ICurrencyPrice): boolean =>
                        c.isoCode === User.currencyType?.isoCode,
                    );
                  const priceWithTax: ICountryCurrencyPrice | undefined =
                    productPrice?.pricesWithTax.find(
                      (p: ICountryCurrencyPrice): boolean =>
                        p.isoCode === (User.countryType?.isoCode || 'CZE'),
                    );

                  const productName: string =
                    product.names.find(
                      (n: ITranslation): boolean => n.code === languageCode,
                    )?.value || '';

                  return (
                    <li key={product.uuid} className="minicart__item">
                      <span className="minicart__item-name">{productName}</span>
                      <span className="minicart__item-price">
                        {product.count}
                        {' × '}
                        {currency && priceWithTax
                          ? formatCurrency(priceWithTax.price, currency)
                          : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <Link
                to={`/${languageCode.toLowerCase()}/basket`}
                className="minicart__cta"
              >
                {t('navigation.title.basket')}
              </Link>
            </div>
          )}
        </div>

        <button
          type="button"
          className="menu-toggle"
          aria-label="Toggle navigation menu"
          aria-expanded={Config.showSidebar}
          aria-controls="sidebar-nav"
          onClick={handleMenuToggle}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
          </svg>
        </button>

        <button
          type="button"
          className="theme-toggle"
          aria-label={
            effectiveTheme === 'dark'
              ? 'Switch to light mode'
              : 'Switch to dark mode'
          }
          onClick={handleThemeToggle}
        >
          {effectiveTheme === 'dark' ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z" />
            </svg>
          )}
        </button>

        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(): void => {}}
        >
          {(): React.JSX.Element => (
            <Form>
              <div className="select">
                <label htmlFor="currency-simple">
                  <Trans>layout.currency</Trans>
                </label>
                <Field
                  as="select"
                  name="currency"
                  id="currency-simple"
                  onChange={({ target }): void => {
                    const ct: ICustomerCurrencyType | undefined =
                      Currency.currencies.find(
                        (c: ICustomerCurrencyType): boolean =>
                          c.isoCode === target.value,
                      );

                    if (ct) {
                      dispatch(
                        changeCurrencyType({
                          currencyType: ct,
                        }),
                      );
                    }

                    dispatch(
                      eventSend({
                        type: EEventType.CURRENCY_TYPE_CHANGE,
                        action: EEventContextAction.CHANGE,
                        context: { currencyType: { isoCode: target.value } },
                      }),
                    );
                  }}
                >
                  {Currency.currencies.map(
                    (c: ICustomerCurrencyType): React.JSX.Element => (
                      <option value={c.isoCode} key={c.isoCode}>
                        {`${c.name} (${c.symbol})`}
                      </option>
                    ),
                  )}
                </Field>
              </div>

              <div className="select">
                <label htmlFor="language-simple">
                  <Trans>layout.language</Trans>
                </label>
                <Field
                  as="select"
                  name="languageType.code"
                  id="language-simple"
                  onChange={({ target }): void => {
                    const code: string = target.value;

                    const lt: ILanguageType | undefined =
                      Language.languages.find(
                        (l: ILanguageType): boolean => l.code === code,
                      );

                    if (lt) {
                      dispatch(
                        changeLanguageType({
                          languageType: lt,
                        }),
                      );
                    }

                    let fullPath: string = `/${code.toLowerCase()}`;

                    if (['p', 'product'].includes(path)) {
                      const slug: ITranslation | undefined =
                        Product.product?.slugs.find(
                          (s: ITranslation): boolean => s.code === code,
                        );
                      const productUUID: string | undefined =
                        Product.product?.uuid;

                      if (slug?.value) {
                        fullPath += `/p/${slug.value}`;
                      } else if (productUUID) {
                        fullPath += `/product/${productUUID}`;
                      }
                    } else if (['c', 'category'].includes(path)) {
                      const category: ICategory | undefined =
                        Category.categories.find((c: ICategory): boolean =>
                          path === 'category'
                            ? c.uuid === identifier
                            : (c.slugs?.some(
                                (s: ITranslation): boolean =>
                                  s.code === languageCode &&
                                  s.value === identifier,
                              ) ?? false),
                        );

                      if (category) {
                        fullPath = getCategoryUrl(category, code);
                      } else if (identifier) {
                        fullPath += `/${path}/${identifier}`;
                      }
                    } else {
                      if (path) {
                        fullPath += `/${path}`;
                      }

                      if (identifier) {
                        fullPath += `/${identifier}`;
                      }

                      if (restSegments.length > 0) {
                        fullPath += `/${restSegments.join('/')}`;
                      }
                    }

                    dispatch(
                      eventSend({
                        type: EEventType.LANGUAGE_TYPE_CHANGE,
                        action: EEventContextAction.CHANGE,
                        context: { languageType: { code: target.value } },
                      }),
                    );

                    window.location.href = fullPath + location.search;
                  }}
                >
                  {languages.map((l: ILanguageType): React.JSX.Element => (
                    <option value={l.code} key={l.code}>
                      {`${l.name} (${l.code})`}
                    </option>
                  ))}
                </Field>
              </div>

              <div className="select">
                <label htmlFor="country-simple">
                  <Trans>layout.destinationCountry</Trans>
                </label>
                <Field
                  as="select"
                  name="countryType.isoCode"
                  id="country-simple"
                  onChange={({ target }): void => {
                    const countryTypeIsoCode: string = target.value;

                    const countryType: ICountry | undefined =
                      Country.countries.find(
                        (c: ICountry): boolean =>
                          c.isoCode === countryTypeIsoCode,
                      );

                    if (countryType) {
                      dispatch(
                        changeCountryType({
                          countryType,
                        }),
                      );

                      dispatch(
                        setDestinationCountryTypeIsoCode({
                          destinationCountryTypeIsoCode: countryTypeIsoCode,
                        }),
                      );
                    }

                    dispatch(
                      eventSend({
                        type: EEventType.COUNTRY_TYPE_CHANGE,
                        action: EEventContextAction.CHANGE,
                        context: { currencyType: { isoCode: target.value } },
                      }),
                    );
                  }}
                >
                  {Country.countries.map((c: ICountry): React.JSX.Element => (
                    <option value={c.isoCode} key={c.isoCode}>
                      {getFlag({ countryTypeIsoCode: c.isoCode })} {c.name} (
                      {c.isoCode})
                    </option>
                  ))}
                </Field>
              </div>
            </Form>
          )}
        </Formik>

        <div className="delivery-info-banner">
          {User.countryType?.isoCode &&
            deliveryPrice &&
            deliveryTime &&
            t('layout.deliveryInfoBanner', {
              flag: getFlag({ countryTypeIsoCode: User.countryType.isoCode }),
              deliveryPrice,
              deliveryTime,
            })}
        </div>

        <div className="account-nav" ref={accountNavRef}>
          <button
            type="button"
            className={`account-nav__btn${showAccountDropdown ? ' account-nav__btn--open' : ''}`}
            aria-label={
              Account.isSignedIn
                ? t('account.navigation.myAccount')
                : t('account.navigation.signIn')
            }
            aria-expanded={Account.isSignedIn ? showAccountDropdown : undefined}
            onClick={(): void => {
              if (Account.isSignedIn) {
                setShowAccountDropdown((prev) => !prev);
              } else {
                setShowSignInModal(true);
              }
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </button>

          {Account.isSignedIn && showAccountDropdown && (
            <div className="account-nav__dropdown">
              <Link
                to={`/${Language.languageType.code.toLowerCase()}/account`}
                onClick={(): void => setShowAccountDropdown(false)}
              >
                {t('account.navigation.myAccount')}
              </Link>
              <Link
                to={`/${Language.languageType.code.toLowerCase()}/account/orders`}
                onClick={(): void => setShowAccountDropdown(false)}
              >
                {t('account.navigation.orders')}
              </Link>
              <a
                href={`/${Language.languageType.code.toLowerCase()}/account/logout`}
                onClick={(): void => setShowAccountDropdown(false)}
              >
                {t('account.navigation.signOut')}
              </a>
            </div>
          )}
        </div>
      </header>

      {showSignInModal && !Account.isSignedIn && (
        <Suspense fallback={null}>
          <SignInModal onClose={(): void => setShowSignInModal(false)} />
        </Suspense>
      )}
    </>
  );
}

const mapStateToProps = ({
  Account,
  Basket,
  Category,
  Config,
  Currency,
  Language,
  User,
  Product,
  Country,
}) => ({
  Account,
  Basket,
  Category,
  Config,
  Currency,
  Language,
  User,
  Product,
  Country,
});

export default connect(mapStateToProps)(Topbar);
